import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'core/api/api.service';
import { UtilsService } from 'core/utils/utils.service';
import { UserInfo } from 'core/app-info/user-info.class';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { Sort } from '@angular/material/sort';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { SortDirection } from 'src/app/modules/pos/quotes/entities/sort.enum';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { ExportUtilsService } from 'core/export-utils/export-utils.service';
import {
  ACCOUNT_DASHBOARD_ROUTES,
  CalendarDateRange,
  Contact,
  RECURRENCE_DAYS,
  Touchpoint,
  TouchpointComment,
  TouchpointDelete,
  TouchpointListPayload,
  TouchpointResponse,
  TouchpointSaveResult,
  TouchpointTemplateValues,
  touchpointTypeColors,
  touchpointTypeIcons,
  touchpointTypeMaterialIcons
} from '../entities/touchpoints-entity';

@Injectable({
  providedIn: 'root'
})
export class TouchpointsService {
  touchpointList = new Subject();
  loading$ = new Subject<boolean>();

  touchpointCounter$ = new Subject<any>();

  contactList$ = new Subject<any[]>();
  touchpointTemplateValues$ = new Subject<TouchpointTemplateValues>();
  touchpointNotesChanged$ = new Subject<void>();
  selectedDay;
  userData: UserInfo;
  templateValues: TouchpointTemplateValues;
  private appStrings: AppLocalization;
  private readonly recurrenceDatePipe = new DatePipe('en-US');
  private readonly recurrenceUnits: Record<string, { singular: string, plural: string }> = {
    Daily: { singular: 'day', plural: 'days' },
    Weekly: { singular: 'week', plural: 'weeks' },
    AbsoluteMonthly: { singular: 'month', plural: 'months' }
  };
  private readonly recurrenceDayFields = RECURRENCE_DAYS;

  private touchpointApiUrl = {
    get: '/api/touchpoint/get',
    create: '/api/touchpoint/create',
    save: '/api/touchpoint/edit',
    delete: '/api/touchpoint/delete',
    list: '/api/touchpoint/list'
  };

  private commentApiUrl = {
    create: '/api/touchpoint/comment/create',
    delete: '/api/touchpoint/comment/delete',
    list: '/api/touchpoint/comment/list'
  };

  private outlookCommentApiUrl = {
    create: '/api/outlook/comment/create',
    delete: '/api/outlook/comment/delete',
    list: '/api/outlook/comment/list'
  };

  outlookApiUrl = {
    getTouchpoint: '/api/outlook/touchpoint/get',
    editTouchpoint: '/api/outlook/touchpoint/edit',
    deleteTouchpoint: '/api/outlook/touchpoint/delete',
    deleteContact: '/api/outlook/contact/delete',
    mergeContact: '/api/outlook/contact/merge',
    syncStatus: '/api/outlook/sync-status',
    templateValues: '/api/outlook/templatevalues',
    contactsList: '/api/outlook/contacts'
  };

  private maxAttachmentSize = 10485760;

  private attachmentApiUrl = {
    download: '/api/outlook/attachment/get',
    upload: '/api/outlook/attachment/upload',
    delete: '/api/outlook/attachment/delete'
  };

  private touchpointTemplateApiRequest = {
    url: '/api/touchpoint/templatevalues'
  };

  private getAccountsUrl = '/api/account/list/byEmployee';

  private getCustomerShippingAddressUrl = '/api/customer/shippingAddress';

  constructor(
    private apiService: ApiService,
    private router: Router,
    private notificationService: NotificationService,
    private appInfoService: AppInfoService,
    private utilsService: UtilsService,
    private exportUtilsService: ExportUtilsService,
  ) {
    this.appStrings = this.appInfoService.appStrings;
    this.initTemplateValues();
  }

  getDateLabel(date: string | Date, showTime?: boolean) {
    date = new Date(date);
    const localeOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    if (showTime) {
      localeOptions['hour'] = '2-digit';
      localeOptions['minute'] = '2-digit';
    }
    return date.toLocaleString('en-US', localeOptions);
  }

  getChangedDay(date: string | Date, direction?: number) {
    const modifier = direction ? direction : 0;
    const day = new Date(date);
    day.setDate(day.getDate() + modifier);
    return day;
  }

  /**
   * Get Customers and Leads of an Account Manager
   * @param accountManagerEmployeeId - The Account Manager employee id
   */
  getCustomersAndLeads(accountManagerEmployeeId: string) {
    return this.apiService.get<Customer[]>(`/api/accountmanager/customersAndLeads/${accountManagerEmployeeId}`);
  }

  getWeekForDay(date: Date | string): CalendarDateRange {
    const start = new Date(date);
    const end = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() + (6 - end.getDay()));
    end.setHours(23, 59, 59, 999);
    return {
      StartDate: start,
      EndDate: end
    };
  }

  /**
   * Gets the full visible month grid range for a day, matching the DS event calendar month view
   * (starts on the Sunday before the 1st, ends on the Saturday completing the last week).
   * @param date any day inside the target month
   */
  getMonthGridForDay(date: Date | string): CalendarDateRange {
    const target = new Date(date);
    const firstDayOfMonth = new Date(target.getFullYear(), target.getMonth(), 1);
    const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    const gridDayCount = Math.ceil((firstDayOfMonth.getDay() + daysInMonth) / 7) * 7;

    const start = new Date(firstDayOfMonth);
    start.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + gridDayCount - 1);
    end.setHours(23, 59, 59, 999);

    return {
      StartDate: start,
      EndDate: end
    };
  }

  /**
   * Gets a specific touchpoint info
   * @param touchpointId touchpoint id
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  getTouchpoint(touchpointId: string, employeeId?: string) {
    const url = `${this.touchpointApiUrl.get}/${touchpointId}${this.getEmployeeIdQuery(employeeId)}`;
    return this.apiService.get<Touchpoint>(url);
  }

  /**
   * Builds a human readable recurrence summary, e.g. 'Every 2 weeks on Mon, Wed until Sep 30, 2026'.
   * Falls back to the generic recurring label when the pattern details are unknown.
   * @param touchpoint the touchpoint to describe
   */
  getRecurrenceLabel(touchpoint: Touchpoint): string {
    if (!touchpoint?.isRecurring) {
      return '';
    }
    const unit = this.recurrenceUnits[touchpoint.recurrencePattern ?? ''];
    if (!unit) {
      return this.appStrings.label_recurring;
    }
    const interval = touchpoint.recurrenceInterval > 1 ? touchpoint.recurrenceInterval : null;
    const until = this.getRecurrenceEndLabel(touchpoint.recurrenceEndDate);
    const parts = [
      interval ? `Every ${interval} ${unit.plural}` : `Every ${unit.singular}`,
      this.getRecurrenceDaysLabel(touchpoint),
      until ? `until ${until}` : ''
    ];
    return parts.filter(part => part).join(' ');
  }

  private getRecurrenceDaysLabel(touchpoint: Touchpoint): string {
    if (touchpoint.recurrencePattern === 'Weekly') {
      const days = this.recurrenceDayFields.filter(day => touchpoint[day.fieldName] === true).map(day => day.value);
      return days.length ? `on ${days.join(', ')}` : '';
    }
    if (touchpoint.recurrencePattern === 'AbsoluteMonthly' && touchpoint.dayOfMonth) {
      return `on day ${touchpoint.dayOfMonth}`;
    }
    return '';
  }

  private getRecurrenceEndLabel(endDate?: string): string {
    if (!endDate || this.utilsService.isMinDate(endDate)) {
      return '';
    }
    return this.recurrenceDatePipe.transform(
      new Date(endDate),
      this.appInfoService.dateFormat,
      undefined,
      this.appInfoService.dateLocale) ?? '';
  }

  /**
   * Gets outlook calendar (touchpoints) of the logged in Account Manager
   */
  getCalendar() {
    return this.apiService.get<any>('/api/outlook/calendar');
  }

  /**
   * Gets outlook contacts of the logged in Account Manager
   * @param sort - The sort change event
   */
  getContacts(sort?: Sort) {
    if (sort?.direction === '') {
      sort.active = '';
    }
    const sortInfo = {
      sort: {
        sortBy: Number(sort?.active) || 0,
        direction: SortDirection[sort?.direction] || 0
      }
    };
    return this.apiService.post<any>(this.outlookApiUrl.contactsList, sortInfo);
  }

  getTouchpointList(filterFields: TouchpointListPayload, callback?: any) {
    this.getData(filterFields).subscribe(serverData => {
      const responseData = serverData.touchpoints;
      responseData.forEach((touchpoint: Touchpoint) => {
        touchpoint.touchpointTypeLabel = this.templateValues.TouchpointType[touchpoint.touchpointType];
        touchpoint.touchpointTypeIcon = touchpointTypeIcons[touchpoint.touchpointType];
        touchpoint.touchpointTypeColor = touchpointTypeColors[touchpoint.touchpointType];
        touchpoint.touchpointTypeMaterialIcon = touchpointTypeMaterialIcons[touchpoint.touchpointType];

        const isoStartDate = this.getDateLabel(touchpoint.startDate, true);
        touchpoint.touchpointStartDateLabel = new Date(isoStartDate);

        const isoEndDate = this.getDateLabel(touchpoint.endDate, true);
        touchpoint.touchpointEndDateLabel = new Date(isoEndDate);

        const contactsArray = (touchpoint.firstContactName ?? '').split(', ').filter(Boolean);
        touchpoint.firstContactDisplay = contactsArray[0] ?? '';
        touchpoint.moreContactsLength = Math.max(contactsArray.length - 1, 0);
      });
      this.touchpointList.next(responseData);
      this.touchpointCounter$.next({
        count: serverData.count,
        total: serverData.total
      });
      if (callback) {
        callback();
      }
    });
  }

  initTemplateValues() {
    if (this.templateValues) {
      this.touchpointTemplateValues$.next(this.templateValues);
    } else {
      const request = this.apiService.get(this.touchpointTemplateApiRequest.url);
      request.subscribe(value => {
        this.templateValues = value;
        this.touchpointTemplateValues$.next(this.templateValues);
      });
    }
  }

  /**
   * if not set, assign the template values to the response.
   * returns an observable
   */
  getTemplateValues() {
    if (!this.templateValues) {
      const request = this.apiService.get(this.touchpointTemplateApiRequest.url);
      request.subscribe(value => {
        this.templateValues = value;
        this.touchpointTemplateValues$.next(this.templateValues);
      });
    }
    return of(this.templateValues);
  }

  /**
   * the api request needed to get the data from the BE
   * @param filterFields the filter value
   */
  private getData(filterFields: TouchpointListPayload) {
    return this.apiService.post<TouchpointResponse>(this.touchpointApiUrl.list, filterFields);
  }

  /**
   * Creates or updates a touchpoint. Outlook-only touchpoints have no CRM record yet, so their
   * edits go to the Outlook endpoint instead of the CRM one.
   * @param touchpointData the touchpoint payload
   * @param callback callback invoked after the save completes
   * @param skipNavigation true to stay on the current route instead of redirecting
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   */
  saveTouchpoint(
    touchpointData: Touchpoint,
    callback?: (...args: any[]) => any,
    skipNavigation = false,
    isOutlookOnly = false,
    employeeId?: string
  ) {
    const redirectPath = skipNavigation ? null : this.getRedirectPath(touchpointData);
    const actionUrl = this.getSaveUrl(touchpointData, isOutlookOnly);
    const payload = employeeId ? { ...touchpointData, employeeId } : touchpointData;
    return this.apiService.post<TouchpointSaveResult>(actionUrl, payload)
      .subscribe((response: TouchpointSaveResult) => this.warnAndNavigate(response.warningMessage, redirectPath, callback, response));
  }

  private getSaveUrl(touchpointData: Touchpoint, isOutlookOnly: boolean): string {
    if (isOutlookOnly) {
      return this.outlookApiUrl.editTouchpoint;
    }
    return touchpointData.id ? this.touchpointApiUrl.save : this.touchpointApiUrl.create;
  }

  /**
   * Getting redirect path based on customer type
   * @param touchpointData touchpoint data
   * @returns path
   */
  getRedirectPath(touchpointData: Touchpoint) {
    if (touchpointData.customerId) {
      return [ACCOUNT_DASHBOARD_ROUTES.customer, touchpointData.customerId, { tab: 'touchpoints' }];
    } else if (touchpointData.leadId) {
      return [ACCOUNT_DASHBOARD_ROUTES.lead, touchpointData.leadId, { tab: 'touchpoints' }];
    } else {
      return ['/touchpoints'];
    }
  }

  /**
   * Account dashboard URL of the customer or lead a touchpoint belongs to
   * @param touchpoint touchpoint data
   * @returns customer or lead dashboard URL, or null when the touchpoint has no associated account
   */
  getAccountDashboardUrl(touchpoint: Touchpoint): string | null {
    const accountId = touchpoint.customerId || touchpoint.leadId;
    if (!accountId) {
      return null;
    }

    const baseRoute = touchpoint.customerId ? ACCOUNT_DASHBOARD_ROUTES.customer : ACCOUNT_DASHBOARD_ROUTES.lead;
    return `${baseRoute}/${accountId}`;
  }

  /**
   * Gets contacts for Customer
   * @param customerId the customer id
   */
  getContactsForCustomer(customerId: string) {
    const customerUrl = `/api/customer/contacts/${customerId}`;
    return this.apiService.get<Contact>(customerUrl).subscribe((response: Contact[]) => {
      this.contactList$.next(response);
    });
  }

  /**
   * Gets contacts for Lead
   * @param leadId the lead id
   */
  getContactsForLead(leadId: string) {
    const leadUrl = `/api/lead/contacts/${leadId}`;
    return this.apiService.get<Contact>(leadUrl).subscribe((response: Contact[]) => {
      this.contactList$.next(response);
    });
  }

  /**
   * Gets the shipping address for a customer or lead, used to autofill the touchpoint location
   * @param customerId the customer or lead id
   * @param isLead whether the id belongs to a lead or a customer
   */
  getCustomerLocation(customerId: string, isLead: boolean) {
    return this.apiService.get<string>(`${this.getCustomerShippingAddressUrl}/${customerId}/${isLead}`, { responseType: 'text' });
  }

  /**
   * Deletes a touchpoint. Outlook-only touchpoints have no CRM record yet, so they go through the
   * Outlook endpoint instead of the CRM one.
   * @param touchpointId id of the touchpoint to delete
   * @param updateRecurrence true to delete the whole series
   * @param redirectPath route to navigate to once deleted
   * @param callback callback invoked after the delete completes
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  deleteTouchpoint(
    touchpointId: string,
    updateRecurrence = false,
    redirectPath?: any[],
    callback?: (...args: any[]) => any,
    isOutlookOnly = false,
    employeeId?: string
  ) {
    const payload = {
      id: touchpointId,
      updateRecurrence,
      ...(employeeId ? { employeeId } : {})
    };
    const deleteUrl = isOutlookOnly ? this.outlookApiUrl.deleteTouchpoint : this.touchpointApiUrl.delete;
    return this.apiService.post<TouchpointDelete>(deleteUrl, payload)
      .subscribe((response: TouchpointDelete) => this.warnAndNavigate(response?.warningMessage, redirectPath, callback));
  }

  /**
   * Deletes an Outlook Touchpoint
   * @param touchpointId id of the touchpoint to be removed
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  deleteOutlookTouchpoint(touchpointId: string, employeeId?: string) {
    const payload = {
      id: touchpointId,
      ...(employeeId ? { employeeId } : {})
    };
    return this.apiService.post<any>(this.outlookApiUrl.deleteTouchpoint, payload);
  }

  /**
   * Gets template values info for sort columns
   */
  getOutlookTemplateValues() {
    return this.apiService.get<any>(this.outlookApiUrl.templateValues);
  }

  /**
   * Deletes an Outlook contact
   * @param contactId id of the contact to be removed
   * @param callback callback
   */
  deleteOutlookContact(contactId: string, callback?: (...args: any[]) => any) {
    const payload = {
      id: contactId
    };
    return this.apiService.post<any>(this.outlookApiUrl.deleteContact, payload)
      .subscribe(() => this.warnAndNavigate(this.appStrings.contacts_removeNotification, null, callback));
  }

  /**
   * Gets complete details of an Outlook touchpoint
   * @param touchpointId touchpoint id
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  outlookTouchpointDetails(touchpointId: string, employeeId?: string) {
    const url = `${this.outlookApiUrl.getTouchpoint}/${touchpointId}${this.getEmployeeIdQuery(employeeId)}`;
    return this.apiService.get<Touchpoint>(url);
  }

  private getEmployeeIdQuery(employeeId?: string): string {
    return employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
  }

  private warnAndNavigate(warningMessage: string, redirectPath?: any[], callback?, callbackArg?: any) {
    if (redirectPath) {
      this.router.navigate(redirectPath);
    }
    if (warningMessage) {
      setTimeout(() => this.notificationService.showNotification(warningMessage, NotificationType.Warning, true));
    }
    if (callback) {
      callback(callbackArg);
    }
  }

  /**
   * Merges an outlook contact with an account
   * @param id contact id
   * @param customerId customer id
   * @param leadId lead id
   * @param callback callback
   */
  mergeOutlookContact(id: string, customerId?: string, leadId?: string, callback?) {
    const payload = {
      id,
      customerId,
      leadId,
    };

    return this.apiService.post<any>(this.outlookApiUrl.mergeContact, payload)
      .subscribe(() => {
        this.notificationService.showNotification(this.appStrings.contacts_mergeNotification, NotificationType.Success, false);
        if (callback) {
          callback();
        }
      });
  }

  /**
   * Getting outlook sync status
   */
  getOutlookSyncStatus() {
    return this.apiService.get(this.outlookApiUrl.syncStatus);
  }

  /**
   * Retrieves a filtered list of Accounts
   * @param accountManagerEmployeeId Employee Id of the logged in Account Manager
   * @param searchValues object containing the filter values (name, address, ln status)
   */
  getAccounts(accountManagerEmployeeId: string, searchValues: { name: string, address: string, status: number }) {
    const payload = {
      employeeId: accountManagerEmployeeId,
      ...searchValues
    };

    return this.apiService.post(this.getAccountsUrl, payload);
  }

  fetchTouchpoints(payload: TouchpointListPayload): Observable<Touchpoint[]> {
    return this.apiService.post<TouchpointResponse>(this.touchpointApiUrl.list, payload).pipe(
      map((response: TouchpointResponse) => response.touchpoints)
    );
  }

  /**
   * Gets the comments of a touchpoint
   * @param touchpointId touchpoint id
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   */
  getComments(touchpointId: string, isOutlookOnly = false) {
    return this.apiService.get<TouchpointComment[]>(`${this.getCommentApiUrl(isOutlookOnly).list}/${touchpointId}`);
  }

  /**
   * Creates a comment on a touchpoint
   * @param touchpointId touchpoint id
   * @param detail comment text
   * @param updateRecurrence true to add the comment to the whole series, false for the single occurrence
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   */
  createComment(touchpointId: string, detail: string, updateRecurrence = false, isOutlookOnly = false) {
    const payload = isOutlookOnly
      ? { outlookTouchpointId: touchpointId, detail, updateRecurrence }
      : { touchpointId, detail, updateRecurrence };
    return this.apiService.post<TouchpointComment>(this.getCommentApiUrl(isOutlookOnly).create, payload);
  }

  private getCommentApiUrl(isOutlookOnly: boolean) {
    return isOutlookOnly ? this.outlookCommentApiUrl : this.commentApiUrl;
  }

  notifyTouchpointNotesChanged() {
    this.touchpointNotesChanged$.next();
  }

  /**
   * Deletes a comment of a touchpoint
   * @param commentId comment id
   * @param updateRecurrence true to delete the comment from the whole series, false for the single occurrence
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   */
  deleteComment(commentId: string, updateRecurrence = false, isOutlookOnly = false) {
    return this.apiService.post(this.getCommentApiUrl(isOutlookOnly).delete, { id: commentId, updateRecurrence });
  }

  /**
   * Validates a file against the attachment size limit
   * @param file the file to check
   * @returns the error message to show, or null when the file fits
   */
  getAttachmentSizeError(file: File): string | null {
    return file.size > this.maxAttachmentSize ? this.appStrings.paymentDetails_purchaseOrderSizeError : null;
  }

  /**
   * Uploads an attachment to a touchpoint
   * @param touchpointId id of the touchpoint the file belongs to
   * @param file the file to upload
   * @param updateRecurrence true to attach the file to the whole series, false for the single occurrence
   * @param isOutlookOnly true when the touchpoint exists only in Outlook, not merged into CRM
   * @param employeeId account manager employee id (admin-selected, optional)
   * @returns the id of the newly created attachment
   */
  uploadAttachment(touchpointId: string, file: File, updateRecurrence = false, isOutlookOnly = false, employeeId?: string) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('id', touchpointId);
    formData.append('updateRecurrence', String(updateRecurrence));
    formData.append('isOutlookOnly', String(isOutlookOnly));
    if (employeeId) {
      formData.append('employeeId', employeeId);
    }
    return this.apiService.post<string>(this.attachmentApiUrl.upload, formData);
  }

  /**
   * Downloads an attachment and saves it locally
   * @param attachmentId id of the attachment
   * @param fileName name the file is saved under
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  downloadAttachment(attachmentId: string, fileName: string, employeeId?: string) {
    const url = `${this.attachmentApiUrl.download}/${attachmentId}${this.getEmployeeIdQuery(employeeId)}`;
    return this.apiService.getBlob(url)
      .subscribe((result: ArrayBuffer) => this.exportUtilsService.downloadFile(result, fileName));
  }

  /**
   * Deletes an attachment
   * @param attachmentId id of the attachment to remove
   * @param updateRecurrence true to delete the attachment from the whole series, false for the single occurrence
   * @param employeeId account manager employee id (admin-selected, optional)
   */
  deleteAttachment(attachmentId: string, updateRecurrence = false, employeeId?: string) {
    return this.apiService.post(this.attachmentApiUrl.delete, {
      id: attachmentId,
      updateRecurrence,
      ...(employeeId ? { employeeId } : {})
    });
  }
}
